import { OnboardingData } from '@nextforce/api'
import { Input, makeStyles, Text } from '@rneui/themed'
import { t } from 'app/i18l'
import { font } from 'app/theme'
import React, { useRef } from 'react'
import { Linking, View } from 'react-native'
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view'
import { SafeAreaView } from 'react-native-safe-area-context'

type Props = {
  phoneNumber: string
  onboardingData: OnboardingData
  setOnboardingData: (onboardingData: OnboardingData) => void
  errorMessage?: any
}

export function OnboardView({
  onboardingData,
  setOnboardingData,
  errorMessage,
  phoneNumber,
}: Props): React.ReactElement | null {
  const onChange = (name: keyof OnboardingData, value: string) => {
    setOnboardingData({
      ...onboardingData,
      [name]: value,
    })
  }

  const { fullName, storePhoneNumber, birthMonthDate } = onboardingData

  const styles = useStyles()
  const scrollViewRef = useRef<KeyboardAwareScrollView | null>(null)

  return (
    <SafeAreaView edges={['left', 'right', 'bottom']} style={styles.root}>
      <KeyboardAwareScrollView
        style={styles.container}
        ref={ref => {
          scrollViewRef.current = ref
        }}
        onContentSizeChange={() =>
          errorMessage && scrollViewRef.current?.scrollToEnd(true)
        }
        enableOnAndroid={true}
      >
        <View style={styles.card}>
          <Text style={styles.title}>{`${t('onboardMessage')}`}</Text>
        </View>
        <Text style={styles.label}>{t('enterFirstAndLastName')}</Text>
        <View style={styles.inputContainer}>
          <Input
            style={styles.textInput}
            onChangeText={text => {
              onChange('fullName', text)
            }}
            placeholder={t('firstAndLastName')}
            value={fullName}
          />
        </View>
        <Text style={styles.label}>{t('enterMonthDateOfBirth')}</Text>
        <View style={styles.inputContainer}>
          <Input
            style={styles.textInput}
            onChangeText={text => {
              onChange('birthMonthDate', text)
            }}
            placeholder="mm/dd"
            value={birthMonthDate}
          />
        </View>
        <Text style={styles.label}>{t('enterStorePhoneNumber')}</Text>
        <View style={styles.inputContainer}>
          <Input
            style={styles.textInput}
            onChangeText={text => {
              onChange('storePhoneNumber', text)
            }}
            placeholder={t('storePhoneNumber')}
            maxLength={20}
            keyboardType="phone-pad"
            value={storePhoneNumber}
          />
        </View>
        <Text style={styles.label}>{t('enterYourPhoneNumber')}</Text>
        <View style={styles.inputContainer} pointerEvents="none">
          <Input
            style={styles.textInput}
            value={phoneNumber}
            editable={false}
            maxLength={20}
          />
        </View>
        <Text style={styles.helperText}>
          {t('onboardingHelperMessage')}{' '}
          <Text
            style={styles.link}
            onPress={() => Linking.openURL('mailto:onboarding@nextforce.us')}
          >
            onboarding@nextforce.us
          </Text>
        </Text>
        {errorMessage ? <Text style={styles.error}>{errorMessage}</Text> : null}
      </KeyboardAwareScrollView>
    </SafeAreaView>
  )
}

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
  },
  container: {
    flex: 1,
    backgroundColor: theme.colors?.grey5,
  },
  inputContainer: {
    flexDirection: 'row',
    backgroundColor: theme.colors?.white,
    paddingHorizontal: 10,
    paddingVertical: 15,
    alignItems: 'center',
  },
  textInput: {
    fontSize: 17,
    fontStyle: 'italic',
    color: theme.colors?.grey1,
    paddingHorizontal: 10,
    width: '100%',
  },
  error: {
    margin: 10,
    textAlign: 'center',
    color: theme.colors?.primary,
    fontFamily: font.bold,
    fontSize: 16,
  },
  card: {
    backgroundColor: theme.colors?.white,
    padding: 30,
  },
  title: {
    textAlign: 'center',
    marginVertical: 10,
    fontSize: 18,
    color: theme.colors?.grey1,
  },
  label: {
    margin: 12,
    fontSize: 16,
    color: theme.colors?.grey1,
  },
  conditionsText: {
    margin: 15,
  },
  link: {
    color: theme.colors?.grey1,
    textDecorationLine: 'underline',
  },
  buttonContainer: {
    margin: 15,
  },
  helperText: {
    margin: 15,
    color: theme.colors?.grey1,
  },
}))
